import React, { useState, useEffect } from 'react';
import { m, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import htmlTruncate from 'html-truncate';
import './CreativeEventsSection.css';

const CreativeEventsSection = ({ events, header }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentView, setCurrentView] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('upcoming');

  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);

  useEffect(() => {
    let filtered = events || [];

    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.content?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    const upcoming = [];
    const past = [];

    filtered.forEach(event => {
      const status = getEventStatus(event.eventDate);
      if (status === 'past') {
        past.push(event);
      } else {
        upcoming.push(event);
      }
    });

    upcoming.sort((a, b) => new Date(a.eventDate || 0) - new Date(b.eventDate || 0));
    past.sort((a, b) => new Date(b.eventDate || 0) - new Date(a.eventDate || 0));

    setUpcomingEvents(upcoming);
    setPastEvents(past);
  }, [events, searchTerm]);

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowModal(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
    document.body.style.overflow = 'auto';
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.05 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }
    },
    hover: { y: -4, transition: { duration: 0.25, ease: "easeOut" } }
  };

  const formatDate = (dateString, timeString) => {
    if (!dateString) return timeString || '';
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-US', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
    });
    return timeString ? `${formattedDate} at ${timeString}` : formattedDate;
  };

  const formatDateCompact = (dateString) => {
    if (!dateString) return { day: '--', month: '---', year: '' };
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      year: date.getFullYear()
    };
  };

  const getEventStatus = (dateString) => {
    if (!dateString) return 'upcoming';
    const eventDate = new Date(dateString);
    eventDate.setHours(23, 59, 59, 999);
    const today = new Date();
    if (eventDate < today) return 'past';
    const eventDateStart = new Date(dateString);
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((eventDateStart - todayStart) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'today';
    if (diffDays <= 7) return 'soon';
    return 'upcoming';
  };

  const activeEvents = activeTab === 'upcoming' ? upcomingEvents : pastEvents;

  /* ---- Grid Card ---- */
  const renderEventCard = (event, index) => {
    const truncatedContent = htmlTruncate(event.content || '', 120);
    const eventStatus = getEventStatus(event.eventDate);

    return (
      <m.div
        key={event.id || index}
        variants={cardVariants}
        whileHover="hover"
        className={`event-card-modern ${eventStatus}`}
        onClick={() => handleEventClick(event)}
      >
        <div className={`status-badge ${eventStatus}`}>
          {eventStatus === 'today' && 'Today'}
          {eventStatus === 'soon' && 'Soon'}
          {eventStatus === 'upcoming' && 'Upcoming'}
          {eventStatus === 'past' && 'Past'}
        </div>

        <div className="event-image-container">
          {event.img && typeof event.img === 'string' && event.img.startsWith('http') ? (
            <img src={event.img} alt={event.title} className="event-image-modern" />
          ) : (
            <div className="event-placeholder-modern">
              <div className="placeholder-icon"><i className="fas fa-calendar-star"></i></div>
            </div>
          )}
          <div className="floating-action">
            <button className="quick-view-btn"><i className="fas fa-eye"></i></button>
          </div>
        </div>

        <div className="event-content-modern">
          <div className="event-meta-modern">
            <div className="event-date">
              <i className="fas fa-clock"></i>
              <span>{formatDate(event.eventDate, event.eventTime)}</span>
            </div>
            {(event.city || event.streetAddress) && (
              <div className="event-location">
                <i className="fas fa-map-marker-alt"></i>
                <span>{event.city || event.streetAddress}</span>
              </div>
            )}
          </div>
          <h3 className="event-title-modern">{event.title}</h3>
          <div className="event-description-modern">
            <div dangerouslySetInnerHTML={{ __html: truncatedContent }} />
          </div>
          <div className="event-actions-modern">
            <button className="learn-more-btn">
              <span>Learn More</span>
              <i className="fas fa-arrow-right"></i>
            </button>
            {eventStatus !== 'past' ? (
              <Link
                to={`/event-registration/${event.id || index}`}
                className="register-btn-modern"
                onClick={(e) => e.stopPropagation()}
              >
                <i className="fas fa-plus"></i> Register
              </Link>
            ) : (
              <span className="register-btn-modern disabled" style={{ background: '#E8E4DE', color: '#7A7A8C', cursor: 'not-allowed' }}>
                Event Ended
              </span>
            )}
          </div>
        </div>
      </m.div>
    );
  };

  /* ---- List Row ---- */
  const renderListItem = (event, index) => {
    const eventStatus = getEventStatus(event.eventDate);
    const dateCompact = formatDateCompact(event.eventDate);
    const truncatedContent = htmlTruncate(event.content || '', 100);

    return (
      <m.div
        key={event.id || index}
        variants={cardVariants}
        whileHover="hover"
        className={`event-list-item ${eventStatus}`}
        onClick={() => handleEventClick(event)}
      >
        {/* Date Column */}
        <div className="list-date-col">
          <span className="list-date-month">{dateCompact.month}</span>
          <span className="list-date-day">{dateCompact.day}</span>
          <span className="list-date-year">{dateCompact.year}</span>
        </div>

        {/* Accent Line */}
        <div className={`list-accent-line ${eventStatus}`}></div>

        {/* Image */}
        {event.img && typeof event.img === 'string' && event.img.startsWith('http') && (
          <div className="list-image-col">
            <img src={event.img} alt={event.title} />
          </div>
        )}

        {/* Content */}
        <div className="list-content-col">
          <div className="list-meta">
            <span className={`list-status-pill ${eventStatus}`}>
              {eventStatus === 'today' ? 'Today' : eventStatus === 'soon' ? 'Soon' : eventStatus === 'upcoming' ? 'Upcoming' : 'Past'}
            </span>
            <span className="list-time">
              <i className="fas fa-clock"></i> {event.eventTime || '—'}
            </span>
            {event.city && (
              <span className="list-location">
                <i className="fas fa-map-marker-alt"></i> {event.city}
              </span>
            )}
          </div>
          <h4 className="list-title">{event.title}</h4>
          <div className="list-desc" dangerouslySetInnerHTML={{ __html: truncatedContent }} />
        </div>

        {/* Actions */}
        <div className="list-actions-col">
          {eventStatus !== 'past' ? (
            <Link
              to={`/event-registration/${event.id || index}`}
              className="list-register-btn"
              onClick={(e) => e.stopPropagation()}
            >
              Register
            </Link>
          ) : (
            <span className="list-register-btn past">Ended</span>
          )}
          <button className="list-details-btn">View Details</button>
        </div>
      </m.div>
    );
  };

  return (
    <div className="creative-events-section">
      {/* Header */}
      <div className="events-header-modern">
        <m.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="header-content-modern"
        >
          <div className="header-badge"><span>Events</span></div>
          <h1 className="events-title-modern text-gradient">
            {header?.title || "Current Events"}
          </h1>
          <p className="events-subtitle-modern">
            {header?.subtitle || "Discover amazing events and connect with your community"}
          </p>
        </m.div>
      </div>

      {/* Toolbar: Tabs + Search + View Toggle */}
      <m.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="events-toolbar"
      >
        <div className="events-tabs">
          <button
            className={`tab-btn ${activeTab === 'upcoming' ? 'active' : ''}`}
            onClick={() => setActiveTab('upcoming')}
          >
            <i className="fas fa-calendar-alt"></i>
            <span>Upcoming</span>
            {upcomingEvents.length > 0 && (
              <span className="tab-count">{upcomingEvents.length}</span>
            )}
          </button>
          <button
            className={`tab-btn ${activeTab === 'past' ? 'active' : ''}`}
            onClick={() => setActiveTab('past')}
          >
            <i className="fas fa-history"></i>
            <span>Past</span>
            {pastEvents.length > 0 && (
              <span className="tab-count past">{pastEvents.length}</span>
            )}
          </button>
        </div>

        <div className="events-controls">
          <div className="search-input-wrapper">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="view-toggle">
            <button
              className={`view-btn ${currentView === 'grid' ? 'active' : ''}`}
              onClick={() => setCurrentView('grid')}
              title="Grid view"
            >
              <i className="fas fa-th"></i>
            </button>
            <button
              className={`view-btn ${currentView === 'list' ? 'active' : ''}`}
              onClick={() => setCurrentView('list')}
              title="List view"
            >
              <i className="fas fa-list"></i>
            </button>
          </div>
        </div>
      </m.div>

      {/* Events Content */}
      <AnimatePresence mode="wait">
        <m.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {activeEvents.length > 0 ? (
            currentView === 'grid' ? (
              <m.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="events-container-modern grid"
              >
                {activeEvents.map((event, index) => renderEventCard(event, index))}
              </m.div>
            ) : (
              <m.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="events-list-container"
              >
                {activeEvents.map((event, index) => renderListItem(event, index))}
              </m.div>
            )
          ) : (
            <div className="no-events">
              <i className={activeTab === 'upcoming' ? 'fas fa-calendar-plus' : 'fas fa-archive'}></i>
              <h3>{activeTab === 'upcoming' ? 'No upcoming events' : 'No past events'}</h3>
              <p>{activeTab === 'upcoming' ? 'Check back soon for new events!' : 'Past events will appear here.'}</p>
            </div>
          )}
        </m.div>
      </AnimatePresence>

      {/* Modal */}
      <AnimatePresence>
        {showModal && selectedEvent && (
          <div className="event-modal-overlay-modern" onClick={handleCloseModal}>
            <m.div
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 50, scale: 0.95 }}
              transition={{ duration: 0.3, type: 'spring', damping: 25, stiffness: 300 }}
              className="event-modal-modern"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header-modern">
                <div className="modal-header-content">
                  <div className="modal-badge">
                    <span className={`status-indicator ${getEventStatus(selectedEvent.eventDate)}`}></span>
                    {getEventStatus(selectedEvent.eventDate)} Event
                  </div>
                  <h2 className="modal-title-modern">{selectedEvent.title}</h2>
                </div>
                <button className="modal-close-modern" onClick={handleCloseModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <div className="modal-content-modern">
                <div className="modal-layout-grid">
                  <div className="modal-sidebar">
                    {selectedEvent.img && typeof selectedEvent.img === 'string' && selectedEvent.img.startsWith('http') && (
                      <div className="modal-image-section">
                        <img src={selectedEvent.img} alt={selectedEvent.title} className="modal-image-modern" />
                      </div>
                    )}
                    <div className="event-key-details">
                      {(selectedEvent.eventDate || selectedEvent.eventTime) && (
                        <div className="key-detail-item">
                          <div className="icon-box"><i className="far fa-clock"></i></div>
                          <div>
                            <h4>Date & Time</h4>
                            <p>
                              {selectedEvent.eventDate && new Date(selectedEvent.eventDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
                              {selectedEvent.eventTime && ` at ${selectedEvent.eventTime}`}
                            </p>
                          </div>
                        </div>
                      )}
                      {(selectedEvent.streetAddress || selectedEvent.city) && (
                        <div className="key-detail-item">
                          <div className="icon-box"><i className="fas fa-map-marker-alt"></i></div>
                          <div>
                            <h4>Location</h4>
                            <p>{[selectedEvent.streetAddress, selectedEvent.city, selectedEvent.country].filter(Boolean).join(', ')}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="modal-main-content">
                    <div className="modal-description">
                      <h3>About this Event</h3>
                      <div className="description-content" dangerouslySetInnerHTML={{ __html: selectedEvent.content }} />
                    </div>
                    <div className="modal-actions-modern">
                      {getEventStatus(selectedEvent.eventDate) !== 'past' ? (
                        <Link to={`/event-registration/${selectedEvent.id || 0}`} className="primary-action-btn">
                          Register Now <i className="fas fa-arrow-right"></i>
                        </Link>
                      ) : (
                        <button className="primary-action-btn disabled" disabled style={{ background: '#E8E4DE', color: '#7A7A8C', cursor: 'not-allowed', boxShadow: 'none' }}>
                          Registration Closed
                        </button>
                      )}
                      {selectedEvent.contact && (
                        <a href={`mailto:${selectedEvent.contact}`} className="secondary-action-btn">
                          <i className="far fa-envelope"></i> Contact Organizer
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </m.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreativeEventsSection;
